<template>
  <ManagerLayout>
    <div class="manager-canceled">
      <h2 class="page-title">解約ユーザー一覧</h2>
      <p class="page-desc">
        メイン契約が解約済みの学習者です（土日祝 Boost のみのサブスク行は除きます）。登録日は学習者の登録日、解約手続き日は解約手続き完了の記録、サービス停止日は利用終了が確定した日時です。
      </p>

      <section class="manager-section filters-section">
        <div class="card">
          <h3 class="section-title">検索</h3>
          <div class="filters">
            <label class="filter-item filter-search">
              <span class="filter-label">名前・ふりがな</span>
              <input
                v-model="searchQ"
                type="text"
                placeholder="部分一致"
                class="form-input"
                @keyup.enter="loadRows"
              />
            </label>
            <button type="button" class="btn btn-primary" :disabled="loading" @click="loadRows">
              {{ loading ? '読み込み中...' : '検索' }}
            </button>
          </div>
        </div>
      </section>

      <section class="manager-section">
        <div class="card">
          <div class="toolbar">
            <span class="total-note">全 {{ total }} 件</span>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="loading" @click="loadRows">
              更新
            </button>
          </div>
          <div v-if="loading" class="loading-state">読み込み中...</div>
          <div v-else-if="!rows.length" class="empty-state">該当する解約記録がありません</div>
          <div v-else class="table-wrap">
            <table class="data-table" aria-label="解約ユーザー一覧">
              <thead>
                <tr>
                  <th>学習者</th>
                  <th>ふりがな</th>
                  <th>保護者（メール）</th>
                  <th>登録日</th>
                  <th>解約手続き日</th>
                  <th>サービス停止日</th>
                  <th>解約理由</th>
                  <th>ご意見・ご要望</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in rows" :key="r.subscriptionId" class="data-row">
                  <td>{{ r.learnerName || '—' }}</td>
                  <td>{{ r.nameKana || '—' }}</td>
                  <td>{{ r.parentEmail || '—' }}</td>
                  <td class="nowrap">{{ formatDate(r.registeredAt) }}</td>
                  <td class="nowrap">{{ formatDateTime(r.canceledAt) }}</td>
                  <td class="nowrap">{{ formatDateTime(r.serviceSuspendedAt) }}</td>
                  <td>{{ reasonLabel(r.cancellationReason) }}</td>
                  <td class="feedback-cell" :title="r.cancellationFeedback || ''">
                    {{ r.cancellationFeedback || '—' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="rows.length > 0 && rows.length < total" class="load-more">
            <button type="button" class="btn btn-secondary" :disabled="loadingMore" @click="loadMore">
              {{ loadingMore ? '読み込み中...' : 'さらに読み込む' }}
            </button>
          </div>
        </div>
      </section>
    </div>
  </ManagerLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import ManagerLayout from '../../components/layout/ManagerLayout.vue'
import { supabase } from '../../supabaseClient'

const apiBase = import.meta.env.VITE_API_BASE_URL || ''
const PAGE_SIZE = 500

const searchQ = ref('')
const rows = ref([])
const total = ref(0)
const loading = ref(false)
const loadingMore = ref(false)
const currentOffset = ref(0)

const REASON_LABELS = {
  user_request: 'ユーザー申請',
  cost: '料金が高い',
  no_time: '時間がない',
  not_suitable: 'サービスが合わない',
  technical_issues: '技術的な問題',
  goal_achieved: '目標を達成した',
  temporary_pause: '一時的な休止',
  other: 'その他',
  trial_cancel: '体験中の即時解約'
}

function reasonLabel (code) {
  const c = String(code || '').trim()
  if (!c) return '—'
  return REASON_LABELS[c] || c
}

function formatDate (iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit' })
  } catch {
    return '—'
  }
}

function formatDateTime (iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
  } catch {
    return '—'
  }
}

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token
}

function buildParams (offset = 0) {
  const params = new URLSearchParams()
  if (searchQ.value.trim()) params.set('q', searchQ.value.trim())
  params.set('limit', String(PAGE_SIZE))
  params.set('offset', String(offset))
  return params.toString()
}

const loadRows = async () => {
  loading.value = true
  currentOffset.value = 0
  try {
    const token = await getToken()
    const qs = buildParams(0)
    const res = await fetch(`${apiBase}/api/manager/canceled-subscriptions?${qs}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      rows.value = []
      total.value = 0
      return
    }
    rows.value = data.rows || []
    total.value = data.total ?? 0
    currentOffset.value = rows.value.length
  } finally {
    loading.value = false
  }
}

const loadMore = async () => {
  const nextOffset = currentOffset.value
  if (nextOffset >= total.value) return
  loadingMore.value = true
  try {
    const token = await getToken()
    const qs = buildParams(nextOffset)
    const res = await fetch(`${apiBase}/api/manager/canceled-subscriptions?${qs}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) return
    const list = data.rows || []
    rows.value = [...rows.value, ...list]
    currentOffset.value = nextOffset + list.length
  } finally {
    loadingMore.value = false
  }
}

onMounted(() => {
  loadRows()
})
</script>

<style scoped>
.manager-canceled { padding: 0; }

.page-title { margin: 0 0 0.5rem 0; font-size: 1.5rem; color: #1e293b; }
.page-desc { margin: 0 0 1.5rem 0; color: #64748b; font-size: 0.9rem; line-height: 1.5; }
.page-desc code { font-size: 0.85rem; background: #f1f5f9; padding: 0.1rem 0.35rem; border-radius: 4px; }
.manager-section { margin-bottom: 2rem; }
.section-title { margin: 0 0 0.75rem 0; font-size: 1.1rem; color: #334155; }

.card { background: white; border-radius: 8px; padding: 1.25rem; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
.filters { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 1rem; }
.filter-item { display: flex; flex-direction: column; gap: 0.25rem; }
.filter-label { font-size: 0.85rem; color: #64748b; font-weight: 500; }
.filter-search .form-input { min-width: 200px; }
.form-input { padding: 0.4rem 0.6rem; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 0.9rem; }
.toolbar { display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem; }
.total-note { font-size: 0.9rem; color: #64748b; }
.btn { padding: 0.4rem 0.9rem; border-radius: 6px; font-size: 0.9rem; cursor: pointer; border: 1px solid transparent; }
.btn-primary { background: #3b82f6; color: white; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #e2e8f0; color: #334155; }
.btn-sm { padding: 0.25rem 0.5rem; font-size: 0.85rem; }
.loading-state, .empty-state { padding: 2rem; text-align: center; color: #64748b; }
.table-wrap { overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.data-table th, .data-table td { padding: 0.5rem 0.75rem; text-align: left; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
.data-table th { background: #f8fafc; color: #475569; font-weight: 600; }
.data-table td.nowrap { white-space: nowrap; }
.feedback-cell { max-width: 14rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.load-more { margin-top: 1rem; text-align: center; }
</style>
